import React, { useEffect, useMemo, useState } from 'react';

import { CompareContractsOutput } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Toggle from '@amzn/meridian/toggle';

import { ColDef, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import { isEmpty, isNil } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { BreadcrumbTablePicker, TreeTablePicker } from 'src/components/CompareContracts/TablePickers';
import {
    ExtractResult,
    TableData,
    TableResult,
    extractTables
} from 'src/components/CompareContracts/utils/comparison-extractor';
import { getLength, getTableWithLargestDiff } from 'src/components/CompareContracts/utils/comparison-utils';
import { IdMappers } from 'src/components/CompareContracts/utils/useIdMappers';
import { useSearchParam } from 'src/hooks';

export interface ICompareByTable {
    comparison: CompareContractsOutput;
    formatters: IdMappers;
}

/**
 * Displays the result of /compareContracts by grouping changes according to the
 * table they come from.
 *
 * Each table can have "left", "center", and "right" elements which represent the
 * unmatched left rows, the matched rows appearing on both sides, and unmatched
 * right rows.
 * @param comparison The output of /compareContracts to display
 * @param formatters Table value formatters for adding display names to unique ID columns
 */
export const CompareByTable: React.FC<ICompareByTable> = ({ comparison, formatters }) => {
    const comparisons: ExtractResult = useMemo(() => extractTables(comparison), [comparison]);
    const [diffType, setDiffType] = useState<keyof TableResult>('center');
    const [showMatchingData, setShowMatchingData] = useState(false);
    const [rawTable, setCurrentTable] = useSearchParam('table');
    const currentTable = rawTable ?? '';
    const largestDiff = useMemo(() => getTableWithLargestDiff(comparisons), [comparisons]);

    const allTypes: (keyof TableResult)[] = ['left', 'center', 'right'];
    const isValidTable: boolean = Object.keys(comparisons).includes(currentTable);
    const isValidDiffType: boolean =
        isValidTable && isAllowedType(comparisons, currentTable, diffType, showMatchingData);
    const allowedTypes = allTypes.filter((type) => {
        return isValidTable && isAllowedType(comparisons, currentTable, type, showMatchingData);
    });

    useEffect(() => {
        if (isNil(rawTable) || isEmpty(rawTable) || !isValidTable) {
            if (!isNil(largestDiff)) {
                setCurrentTable(largestDiff);
            }
        }
    }, [comparisons]);

    useEffect(() => {
        if (!isValidDiffType && allowedTypes.length > 0) {
            setDiffType(allowedTypes[0]);
        }
    }, [currentTable, diffType, allowedTypes]);

    return (
        <Column heights={['fit', 'fill']} height={'100%'}>
            <Row widths={['fit', 'fill', 'fit', 'fit']}>
                <TreeTablePicker
                    table={currentTable}
                    setTable={setCurrentTable}
                    comparison={comparison}
                    extract={comparisons}
                />
                <BreadcrumbTablePicker
                    table={currentTable}
                    setTable={setCurrentTable}
                    comparison={comparison}
                    extract={comparisons}
                />
                <Toggle checked={showMatchingData} onChange={setShowMatchingData}>
                    Show matching data
                </Toggle>
                <ButtonGroup value={diffType} onChange={setDiffType}>
                    {allTypes.map((diff) => (
                        <ButtonOption value={diff} key={diff} disabled={!allowedTypes.includes(diff)}>
                            {diff}
                        </ButtonOption>
                    ))}
                </ButtonGroup>
            </Row>
            <Row height={'100%'}>
                {isValidTable && isValidDiffType && (
                    <DiffTable
                        data={comparisons[currentTable][diffType]!}
                        showMatchingData={showMatchingData}
                        formatters={formatters}
                    />
                )}
            </Row>
        </Column>
    );
};

function isAllowedType(
    comparisons: ExtractResult,
    table: string,
    type: keyof TableResult,
    includeMatchingData: boolean
): boolean {
    return !isNil(comparisons[table][type]) && getLength(comparisons[table][type]?.rows, includeMatchingData) > 0;
}

interface IDiffTable {
    data: TableData;
    showMatchingData: boolean;
    formatters: { [table: string]: ValueFormatterFunc };
}

const hiddenColumns = new Set(['CHANGED_BY', 'CHANGE_DATE', 'IS_DELETED', 'CREATED_BY', 'CREATION_DATE']);

/**
 * Internal component for showing diff data in a tabular format.
 * @param data The diff data to show.
 * @param showMatchingData True if the table should show data from matching rows.
 * @param formatters Functions that change the visual contents of the table
 * based on the raw diff data.
 */
const DiffTable: React.FC<IDiffTable> = ({ data, showMatchingData, formatters }) => {
    const rowData = useMemo(() => {
        if (showMatchingData) {
            return data.rows;
        } else {
            return data.rows.filter((row) => !row.isExactMatch);
        }
    }, [data, showMatchingData]);
    const columnDefs: ColDef[] = useMemo(() => {
        const sharedCols = data.matchingColumns.map((col) => ({
            headerName: col,
            field: `data.${col}`,
            valueFormatter: formatters[col],
            initialHide: hiddenColumns.has(col)
        }));
        const diffCols = data.nonMatchingColumns.map((col) => ({
            headerName: col,
            children: [
                {
                    headerName: 'left',
                    field: `changedData.${col}.left`,
                    valueFormatter: formatters[col],
                    initialHide: hiddenColumns.has(col)
                },
                {
                    headerName: 'right',
                    field: `changedData.${col}.right`,
                    valueFormatter: formatters[col],
                    initialHide: hiddenColumns.has(col)
                }
            ]
        }));
        const contextCols = {
            headerName: 'context',
            children: data.contextColumns.map((col) => ({
                headerName: col,
                field: `contextData.${col}`,
                valueFormatter: formatters[col],
                initialHide: hiddenColumns.has(col)
            }))
        };
        return [...sharedCols, ...diffCols, contextCols];
    }, [data, formatters]);
    return <BaseAgGrid columnDefs={columnDefs} rowData={rowData} suppressSizeColumnsToFit={true} />;
};
